"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useConvexAuth, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { ShoppingCart, Check, ArrowRight } from "@/components/icons";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCart } from "@/hooks/cart/use-cart";
import { track } from "@/lib/analytics/tracker";
import { buildGA4Item } from "@/lib/analytics/ga4/items";
import { trackGA4AddToCart } from "@/lib/analytics/ga4/ecommerce";
import { handleError } from "@/lib/handle-error";

interface AddToCartButtonProps {
  productId: Id<"products">;
  quantity: number;
  productPublicIdentifier?: string;
  unitPriceCents: number;
  productTitle: string;
  productTypeSlug: string;
  disabled?: boolean;
  onAdded?: () => void;
  variant?: "outline" | "primary" | "ghost";
  label?: string;
  trackCartState?: boolean;
}

export function AddToCartButton(props: AddToCartButtonProps) {
  const {
    productId,
    quantity,
    disabled = false,
    variant = "outline",
    label = "Add to Cart",
    trackCartState = true,
  } = props;
  const { addToCart, inCart, cartQuantity } = useProductCartState(
    productId,
    trackCartState,
  );
  const { isLoading, handleClick } = useAddToCartClick(props, addToCart);
  const isCurrentQuantity = inCart && cartQuantity === quantity && !disabled;

  if (isCurrentQuantity) return <InCartLink cartQuantity={cartQuantity} />;

  return (
    <Button
      type="button"
      variant={variant}
      className="w-full"
      disabled={isLoading || disabled}
      onClick={handleClick}
      size="default"
    >
      {inCart ? (
        <>
          <ShoppingCart className="size-4" />
          Update Cart ({cartQuantity} → {quantity})
        </>
      ) : (
        <>
          <ShoppingCart className="size-4" />
          {label}
        </>
      )}
    </Button>
  );
}

function useProductCartState(
  productId: Id<"products">,
  trackCartState: boolean,
) {
  const { isAuthenticated } = useConvexAuth();
  const { addToCart, getItemForProduct } = useCart();

  const serverCartState = useQuery(
    api.cart.queries.isInCart,
    isAuthenticated && trackCartState ? { productId } : "skip",
  );

  const cartState = isAuthenticated
    ? serverCartState
    : getItemForProduct(productId);
  const inCart = cartState?.inCart === true;
  const cartQuantity = cartState?.inCart ? cartState.quantity : 0;
  return { addToCart, inCart, cartQuantity };
}

function useAddToCartClick(
  props: AddToCartButtonProps,
  addToCart: ReturnType<typeof useCart>["addToCart"],
) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsLoading(true);
    try {
      await addAndAnnounce(props, addToCart, router);
    } catch (err) {
      handleError(err, {
        display: "banner",
        fallbackMessage: "Failed to add to cart",
        reportIssueContext: { titlePrefix: "Add to cart failed" },
      });
    } finally {
      setIsLoading(false);
    }
  };
  return { isLoading, handleClick };
}

async function addAndAnnounce(
  {
    productId,
    quantity,
    productPublicIdentifier,
    unitPriceCents,
    productTitle,
    productTypeSlug,
    onAdded,
  }: AddToCartButtonProps,
  addToCart: ReturnType<typeof useCart>["addToCart"],
  router: ReturnType<typeof useRouter>,
) {
  const result = await addToCart(productId, quantity, {
    unitPriceCents,
    productTitle,
    productTypeSlug,
  });
  onAdded?.();
  track(result.status === "updated" ? "cart.item_updated" : "cart.item_added", {
    productId,
    quantity,
    productType: productTypeSlug,
  });

  trackCartAddition({
    productId,
    productPublicIdentifier,
    productTitle,
    unitPriceCents,
    quantity,
    productTypeSlug,
  });

  toast.success(
    result.status === "updated" ? "Cart updated" : "Added to cart",
    {
      action: {
        label: "View cart",
        onClick: () => router.push("/cart"),
      },
    },
  );
}

function InCartLink({ cartQuantity }: { cartQuantity: number }) {
  return (
    <Button asChild size="default" variant="outline" className="w-full">
      <Link href="/cart" onClick={(event) => event.stopPropagation()}>
        <Check className="size-4 text-success-strong" />
        In Cart ({cartQuantity}) · View Cart
        <ArrowRight className="size-4" />
      </Link>
    </Button>
  );
}

function trackCartAddition({
  productId,
  productPublicIdentifier,
  productTitle,
  unitPriceCents,
  quantity,
  productTypeSlug,
}: Pick<
  AddToCartButtonProps,
  | "productId"
  | "productPublicIdentifier"
  | "productTitle"
  | "unitPriceCents"
  | "quantity"
  | "productTypeSlug"
>) {
  const ga4Item = buildGA4Item({
    itemId: productPublicIdentifier ?? productId,
    itemName: productTitle,
    priceCents: unitPriceCents,
    quantity,
    category: productTypeSlug,
  });
  trackGA4AddToCart(ga4Item, (unitPriceCents * quantity) / 100);
}
